<template>
   <div id="container">
      <QCard id="card">
         <QCardSection class="bg-green text-white text-h3 text-bold q-pa-lg"
            ><span>{{
               status === "success"
                  ? "Thank You"
                  : status === "loading"
                    ? "Processing"
                    : "Subscription Error"
            }}</span></QCardSection
         >
         <QCardSection class="q-pa-xl text-center">
            <div v-if="status === 'loading'">
               <div class="text-h6 text-grey-7 q-mb-lg">
                  We're finalizing your subscription...
               </div>
               <QSpinner color="primary" size="3em" class="q-mb-lg" />
               <div class="text-body2 text-grey-6 q-mb-sm">
                  This may take up to
                  {{ (maxAttempts * pollInterval) / 1000 }} seconds.
               </div>
               <div class="text-caption text-grey-5">
                  Attempt {{ currentAttempt }} of {{ maxAttempts }}
               </div>
            </div>

            <div v-else-if="status === 'success'">
               <div v-if="trialInfo">
                  <div class="text-h4 q-mb-lg">🎉 Welcome to ezbot.ai!</div>
                  <div class="text-h6 text-grey-8 q-mb-md">
                     You've successfully started your
                     <span class="text-weight-bold text-primary"
                        >{{ trialInfo.trialDays }}-day free trial</span
                     >
                     of the
                     <span class="text-weight-bold"
                        >{{ trialInfo.tierName }} tier</span
                     >.
                  </div>
                  <div class="text-body1 text-grey-7 q-mb-xl">
                     Your trial includes
                     <span class="text-weight-bold"
                        >{{ trialInfo.predictions }} predictions</span
                     >
                     to get you started.
                  </div>
                  <QBtn
                     color="primary"
                     size="lg"
                     label="START OPTIMIZING"
                     class="q-px-xl q-py-md text-weight-bold"
                     unelevated
                     @click="goToOnboarding"
                  />
               </div>
               <div v-else class="text-body1 text-grey-7">
                  You will receive an email with login instructions shortly. If
                  you need help, please contact
                  <a href="mailto:support@ezbot.ai" class="text-primary"
                     >support@ezbot.ai</a
                  >
               </div>
            </div>

            <div v-else>
               <div class="text-h6 text-negative q-mb-md">
                  We couldn't verify your subscription status.
               </div>
               <div class="text-body2 text-grey-7 q-mb-sm">
                  This could be due to delays in processing your payment.
               </div>
               <div class="text-body2 text-grey-7 q-mb-lg">
                  Please contact
                  <a href="mailto:support@ezbot.ai" class="text-primary"
                     >support@ezbot.ai</a
                  >
                  if this issue persists.
               </div>
               <QBtn
                  color="primary"
                  label="Try Again"
                  size="md"
                  unelevated
                  @click="startPolling"
               />
            </div>
         </QCardSection>
      </QCard>
      <Teleport
         v-if="status === 'success' && stageWidth && stageHeight"
         to="body"
      >
         <div id="confetti">
            <ConfettiExplosion
               :stage-height="stageHeight"
               :stage-width="stageWidth"
            />
         </div>
      </Teleport>
   </div>
</template>

<script setup lang="ts">
import { trackEvent } from "@/analytics/trackEvent"
import { onMounted, ref, onBeforeUnmount, computed } from "vue"
import ConfettiExplosion from "vue-confetti-explosion"
import {
   clearAuthCache,
   hasActiveStripeSubscription,
   getOrg,
} from "@/services/clerk"
import { useRouter } from "vue-router"
import { useOnboardingStore } from "@/stores/onboardingStore"
import { clearAllState } from "@/utils/stateManagement"
import { collectError } from "@utils/errors"
import { toast } from "vue3-toastify"

const stageHeight = ref<number | null>(null)
const stageWidth = ref<number | null>(null)
const status = ref<"loading" | "success" | "error">("loading")
const currentAttempt = ref(1)
const maxAttempts = 24 // 2 minutes with 5-second intervals
const pollInterval = 5000 // 5 seconds
let pollingTimer: number | null = null
const router = useRouter()
const onboardingStore = useOnboardingStore()

const trialInfo = computed(() => {
   const tier = onboardingStore.tier
   const trialDays = onboardingStore.trial_days

   if (!tier || !trialDays) return null

   const tierName = tier === "starter_tier" ? "Starter" : "Professional"
   const predictions = tier === "starter_tier" ? "35K" : "500K"

   return {
      tierName,
      trialDays,
      predictions,
   }
})

const checkSubscriptionStatus = async () => {
   try {
      // Clear cache and refresh clerk to get the latest status
      clearAuthCache()
      const isActive = await hasActiveStripeSubscription()

      if (currentAttempt.value >= maxAttempts) {
         // Timed out
         status.value = "error"
         trackEvent("stripe_subscription_verification_failed")
         toast.error("Subscription verification timed out")
         return false
      }

      if (!isActive) {
         // Continue polling
         currentAttempt.value++
         return false
      }

      // Successful with Trial
      if (trialInfo.value) {
         status.value = "success"
         trackEvent("stripe_subscription_activated")

         // wait for the trial user to click the button

         return true
      }

      // Successful without Trial
      status.value = "success"
      trackEvent("stripe_subscription_activated")
      await redirectToApp()

      return true
   } catch (error) {
      status.value = "error"
      collectError(error as Error)
      trackEvent("stripe_subscription_verification_error")
      return false
   }
}

const startPolling = () => {
   // Reset state
   status.value = "loading"
   currentAttempt.value = 1

   // Start polling process
   pollForSubscription()
}

const pollForSubscription = async () => {
   const isActive = await checkSubscriptionStatus()

   if (!isActive && status.value === "loading") {
      // Continue polling if not active and still in loading state
      pollingTimer = window.setTimeout(pollForSubscription, pollInterval)
   }
}

const redirectToApp = async () => {
   // Clear all application state before navigation
   clearAllState()

   // Get organization details to include the slug in the route
   const org = await getOrg()
   if (org && org.slug) {
      router.push({ name: "OrgHome", params: { orgSlug: org.slug } })
   } else {
      console.error("Could not get organization details for navigation")
      router.push({ name: "OrgsSetup" }) // Fallback without slug
   }
}

const goToOnboarding = async () => {
   // Get organization and project details for onboarding route
   const org = await getOrg()
   if (!org || !org.slug) {
      console.error("Could not get organization details for onboarding")
      router.push({ name: "OrgsSetup" })
      return
   }

   // Clear all application state after navigation
   await router.push({ name: "CreateProject", params: { orgSlug: org!.slug } })
   clearAllState()
}

onMounted(() => {
   trackEvent("stripe_subscription_started")
   stageHeight.value = window.innerHeight
   stageWidth.value = window.innerWidth

   // Start polling when component mounts
   startPolling()
})

onBeforeUnmount(() => {
   // Clear any pending timers
   if (pollingTimer !== null) {
      clearTimeout(pollingTimer)
   }
})
</script>

<style lang="scss" scoped>
#card {
   width: 100%;
   max-width: 600px;
}
#container {
   display: flex;
   justify-content: center;
   align-items: center;
   height: 100vh;
   background-color: #f5f5f5;
   background-image: linear-gradient(to top right, #007bff, #00aaff);
}
#confetti {
   position: fixed;
   width: 100%;
   height: 100%;
   left: 0;
   top: 0;
   pointer-events: none;
   overflow: hidden;
   display: flex;
   justify-content: center;
   align-items: flex-start;
}
</style>
